import { toAbsoluteUrl } from "./api.js";
import { shapeSvg } from "./shapes.jsx";
import { cssGradient, imageAdjustFilter, maskCss } from "./layerStyle.jsx";

function hexToRgba(hex, opacity) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Mirrors the backend's _LINEAR_DIRECTIONS / _linear_gradient_angle exactly.
const LINEAR_GRADIENT_DIRECTIONS = {
  top: [0, 1],
  bottom: [0, -1],
  left: [1, 0],
  right: [-1, 0],
  "top-left": [1, 1],
  "top-right": [-1, 1],
  "bottom-left": [1, -1],
  "bottom-right": [-1, -1],
};

function linearGradientAngle(position, w, h) {
  const [dx, dy] = LINEAR_GRADIENT_DIRECTIONS[position] || LINEAR_GRADIENT_DIRECTIONS.top;
  const deg = (Math.atan2(dx * w, -dy * h) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

function gradientBackgroundCss(gradient, w, h) {
  const endColor = hexToRgba(gradient.color_end, gradient.end_opacity);
  if (gradient.kind === "linear") {
    const angle = linearGradientAngle(gradient.position, w, h);
    return `linear-gradient(${angle.toFixed(2)}deg, ${gradient.color_start} 0%, ${endColor} 100%)`;
  }
  const position = { center: "center", top: "center top", bottom: "center bottom" }[gradient.position] || "center";
  return `radial-gradient(circle at ${position}, ${gradient.color_start} 0%, ${endColor} 100%)`;
}

// Lightweight read-only preview of a template's JSON — mirrors the backend
// render engine's layout math (absolute positioning, z-order), scaled down.
//
// Always occupies exactly `width`x`height` (a fixed thumbnail slot — square
// by default), regardless of the template's own aspect ratio, so a grid of
// cards stays uniform even when templates mix square (1080x1080) and
// portrait (e.g. 1080x1920) sizes. The design itself is scaled to fit
// within that box (never cropped or stretched) and centered inside it.
export default function TemplatePreview({ template, width = 240, height = width }) {
  const scale = Math.min(width / template.width, height / template.height);
  const contentWidth = template.width * scale;
  const contentHeight = template.height * scale;
  const layers = [...template.layers].filter((l) => !l.hidden).sort((a, b) => a.z_index - b.z_index);

  const bgImage = template.background_image
    ? template.background_image.startsWith("/")
      ? toAbsoluteUrl(template.background_image)
      : template.background_image
    : null;

  const gradient = template.background_gradient;

  return (
    <div
      className="tpl-preview-frame"
      style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
    <div
      className="tpl-preview"
      style={{
        width: contentWidth,
        height: contentHeight,
        position: "relative",
        backgroundColor: template.background_color,
        ...(bgImage && {
          backgroundImage: `url('${bgImage}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }),
      }}
    >
      {gradient && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            background: gradientBackgroundCss(gradient, template.width, template.height),
          }}
        />
      )}
      {layers.map((layer) => {
        const base = {
          position: "absolute",
          left: layer.x * scale,
          top: layer.y * scale,
          width: layer.width * scale,
          height: layer.height * scale,
          transform: `rotate(${layer.rotation || 0}deg)`,
          opacity: layer.opacity ?? 1,
        };

        if (layer.type === "text") {
          return (
            <div
              key={layer.id}
              style={{
                ...base,
                fontFamily: layer.font_family,
                fontSize: Math.max(4, layer.font_size * scale),
                fontWeight: layer.font_weight,
                color: layer.color,
                textAlign: layer.text_align,
                lineHeight: 1.2,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {layer.text}
            </div>
          );
        }

        if (layer.type === "image") {
          const src = layer.src && layer.src.startsWith("/")
            ? toAbsoluteUrl(layer.src)
            : layer.src;
          return (
            <div
              key={layer.id}
              style={{
                ...base,
                overflow: "hidden",
                borderRadius: layer.mask_shape || layer.mask_path ? 0 : (layer.border_radius || 0) * scale,
                background: "#e5e7eb",
                ...(layer.mask_shape || layer.mask_path ? maskCss(layer.mask_shape, layer.mask_path) : {}),
              }}
            >
              {src && (
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  style={{
                    ...(layer.image_crop?.[2] > 0 && layer.image_crop?.[3] > 0
                      ? {
                          position: "absolute",
                          maxWidth: "none",
                          width: `${100 / layer.image_crop[2]}%`,
                          height: `${100 / layer.image_crop[3]}%`,
                          left: `${(-layer.image_crop[0] / layer.image_crop[2]) * 100}%`,
                          top: `${(-layer.image_crop[1] / layer.image_crop[3]) * 100}%`,
                        }
                      : { width: "100%", height: "100%", objectFit: layer.object_fit || "cover" }),
                    display: "block",
                    filter: imageAdjustFilter(layer.image_adjust, scale) || undefined,
                  }}
                />
              )}
            </div>
          );
        }

        if (layer.type === "shape") {
          return (
            <div key={layer.id} style={base}>
              {shapeSvg(layer.shape_kind || "circle", {
                fill: layer.background_color,
                stroke: layer.stroke_color,
                strokeWidth: layer.stroke_width,
                width: layer.width,
                height: layer.height,
                radius: layer.border_radius || 0,
                gradient: layer.fill_gradient,
                dash: layer.stroke_dash,
                columns: layer.grid_columns,
                rows: layer.grid_rows,
                dotColor: layer.dot_color || undefined,
                dotSize: layer.dot_size || undefined,
                id: layer.id,
              })}
            </div>
          );
        }

        if (layer.type === "rect") {
          const radii = layer.corner_radii?.length === 4
            ? layer.corner_radii
            : Array(4).fill(layer.border_radius || 0);
          return (
            <div
              key={layer.id}
              style={{
                ...base,
                background: layer.fill_gradient ? cssGradient(layer.fill_gradient) : layer.background_color || "#cccccc",
                borderRadius: radii.map((r) => `${(r || 0) * scale}px`).join(" "),
                border: layer.stroke_color
                  ? `${Math.max(1, (layer.stroke_width || 1) * scale)}px ${layer.stroke_dash || "solid"} ${layer.stroke_color}`
                  : "none",
              }}
            />
          );
        }

        return null;
      })}
    </div>
    </div>
  );
}
